import { useMemo, useState } from "react";
import { AlertTriangle, Calculator, X, ChevronRight, CheckCircle2, FileWarning } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatNumber } from "@/lib/numberFormatting";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import portelinLogo from "@/assets/portelin.jpg";
import { parseNumericLike } from "@/lib/numberFormatting";
import { docTotals, type IssuedProcessedFile } from "@/lib/issuedProcessing";

/**
 * Asistente Virtual de facturas emitidas.
 * Detecta descuadres de céntimos (que A3 rechaza) y datos que faltan
 * antes de generar el SUENLACE. La corrección la hace el usuario.
 */

type Severity = "high" | "medium" | "low";

interface Finding {
  id: string;
  fileId: string;
  severity: Severity;
  title: string;
  invoice: string;
  detail: string;
  suggestion: string;
  kind: "mismatch" | "missing" | "credit_note";
}

const fmtEUR = (n: number | undefined) =>
  typeof n === "number" && Number.isFinite(n)
    ? n.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €"
    : "—";

const docLabel = (d: IssuedProcessedFile) =>
  `${d.customerName || "—"} · ${[d.series, d.invoiceNumber].filter(Boolean).join("-") || "s/nº"} · ${fmtEUR(parseNumericLike(d.totalAmount))}`;

const computeFindings = (processed: IssuedProcessedFile[]): Finding[] => {
  const findings: Finding[] = [];

  processed.forEach((d) => {
    const total = parseNumericLike(d.totalAmount);
    const { expected } = docTotals(d);
    const diff = Number((total - expected).toFixed(2));

    // Diferencias residuales (hasta 5 céntimos) las ajusta la propia
    // exportación al SUENLACE (balanceVatLines): no se avisan aquí.
    if (total !== 0 && Math.abs(diff) > 0.05) {
      const abs = Math.abs(diff);
      const severity: Severity = abs > 1 ? "high" : "medium";
      findings.push({
        id: `mm-${d.id}`,
        fileId: d.id,
        kind: "mismatch",
        severity,
        title: "El total no cuadra con base + IVA",
        invoice: docLabel(d),
        detail: `Base + IVA + RE − IRPF = ${fmtEUR(expected)}. La factura indica ${fmtEUR(total)} (diferencia ${diff > 0 ? "+" : ""}${formatNumber(diff)} €).`,
        suggestion:
          abs > 1
            ? "Revisa si falta una línea de IVA, un recargo o una retención."
            : "Diferencia significativa — revisa los importes antes de exportar.",
      });
    }

    const missing: string[] = [];
    if (!d.date) missing.push("fecha");
    if (!d.invoiceNumber.trim()) missing.push("nº de factura");
    if (!d.customerAccount.trim()) missing.push("cuenta de cliente (430)");
    if (!d.salesAccount.trim()) missing.push("cuenta de venta (700)");
    if (d.vatLines.length === 0) missing.push("desglose de IVA");
    if (missing.length > 0) {
      findings.push({
        id: `ms-${d.id}`,
        fileId: d.id,
        kind: "missing",
        severity: "high",
        title: "Faltan datos para exportar",
        invoice: docLabel(d),
        detail: `Sin informar: ${missing.join(", ")}.`,
        suggestion: "Completa estos campos en la factura antes de generar el SUENLACE.",
      });
    }
  });

  // Abonos / rectificativas mal detectados
  processed.forEach((d) => {
    const hints = d.creditNoteHints ?? [];
    const level = d.creditNoteHintLevel ?? "none";
    if (d.creditNoteReviewed) {
      // El usuario ya decidió el signo manualmente.
    } else if (!d.isCreditNote && level !== "none") {
      findings.push({
        id: `cn-${d.id}`,
        fileId: d.id,
        kind: "credit_note",
        severity: level === "strong" ? "high" : "medium",
        title: "Posible factura de abono no marcada",
        invoice: docLabel(d),
        detail: `En el documento aparece: ${hints.join(", ")}. Se ha guardado con importes positivos, como una factura corriente.`,
        suggestion: "Abre la factura y, si es un abono, introduce los importes en negativo; si no lo es, puedes ignorar este aviso.",
      });
    } else if (d.isCreditNote && level === "none") {
      findings.push({
        id: `cn-${d.id}`,
        fileId: d.id,
        kind: "credit_note",
        severity: "medium",
        title: "Marcada como abono sin indicios en el documento",
        invoice: docLabel(d),
        detail: "Está marcada como abono/rectificativa, pero el texto del documento no menciona abono ni importes negativos.",
        suggestion: "Confirma que es realmente un abono; si no lo es, pon los importes en positivo.",
      });
    }
  });

  const rank: Record<Severity, number> = { high: 0, medium: 1, low: 2 };
  return findings.sort((a, b) => rank[a.severity] - rank[b.severity]);
};

const severityStyles: Record<Severity, string> = {
  high: "border-destructive/40 bg-destructive/5",
  medium: "border-amber-300/50 bg-amber-50 dark:bg-amber-950/20",
  low: "border-muted bg-muted/30",
};

const severityBadge: Record<Severity, { label: string; variant: "destructive" | "secondary" | "outline" }> = {
  high: { label: "Alta", variant: "destructive" },
  medium: { label: "Media", variant: "secondary" },
  low: { label: "Baja", variant: "outline" },
};

interface Props {
  processed: IssuedProcessedFile[];
  onOpenInvoice: (fileId: string) => void;
}

const IssuedAIValidator = ({ processed, onOpenInvoice }: Props) => {
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const findings = useMemo(() => computeFindings(processed), [processed]);
  const visible = findings.filter((f) => !dismissed.has(f.id));
  const highCount = visible.filter((f) => f.severity === "high").length;

  if (processed.length === 0) return null;

  const handleGoTo = (fileId: string) => {
    setOpen(false);
    setTimeout(() => onOpenInvoice(fileId), 80);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2 rounded-full bg-[hsl(150_65%_38%)] py-2 pl-2 pr-4 text-white shadow-lg ring-1 ring-black/10 transition-transform active:scale-95"
        aria-label="Abrir Asistente Virtual"
      >
        <span className="relative">
          <img
            src={portelinLogo}
            alt="Asistente Virtual"
            className="h-9 w-9 rounded-full object-cover ring-2 ring-white/40"
          />
          {visible.length > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-3 w-3">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-300 opacity-75" />
              <span className="relative inline-flex h-3 w-3 rounded-full bg-amber-400 ring-2 ring-[hsl(150_65%_38%)]" />
            </span>
          )}
        </span>
        <span className="text-sm font-semibold tracking-wide">Asistente Virtual</span>
        {visible.length > 0 && (
          <Badge
            variant="secondary"
            className="ml-1 h-5 min-w-5 bg-white/20 px-1.5 text-xs text-white hover:bg-white/20"
          >
            {visible.length}
          </Badge>
        )}
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-md flex flex-col">
          <SheetHeader className="space-y-2">
            <SheetTitle className="flex items-center gap-2">
              <img
                src={portelinLogo}
                alt="Asistente Virtual"
                className="h-8 w-8 rounded-full object-cover"
              />
              <span>Asistente Virtual</span>
              <span className="text-sm font-normal text-muted-foreground">· facturas emitidas</span>
            </SheetTitle>
            <SheetDescription>
              Revisión de descuadres y datos incompletos antes de generar el SUENLACE.
            </SheetDescription>
          </SheetHeader>

          <div className="my-4 rounded-lg border bg-muted/40 p-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Hallazgos abiertos</span>
              <span className="font-semibold">{visible.length}</span>
            </div>
            {highCount > 0 && (
              <div className="mt-1 flex items-center gap-1.5 text-destructive">
                <AlertTriangle className="h-3.5 w-3.5" />
                <span className="text-xs">{highCount} requieren atención antes de exportar</span>
              </div>
            )}
          </div>

          <div className="flex-1 space-y-3 overflow-y-auto pr-1">
            {visible.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <CheckCircle2 className="mb-3 h-10 w-10 text-emerald-500" />
                <p className="font-medium">Todo en orden</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  No se han detectado anomalías en las facturas de esta sesión.
                </p>
              </div>
            ) : (
              visible.map((f) => {
                const sev = severityBadge[f.severity];
                return (
                  <div key={f.id} className={`rounded-lg border p-3 ${severityStyles[f.severity]}`}>
                    <div className="mb-2 flex items-start gap-2">
                      <div className="mt-0.5 text-muted-foreground">
                        {f.kind === "mismatch" ? (
                          <Calculator className="h-4 w-4" />
                        ) : (
                          <FileWarning className="h-4 w-4" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="mb-1 flex items-center gap-2">
                          <span className="text-sm font-semibold">{f.title}</span>
                          <Badge variant={sev.variant} className="text-[10px]">
                            {sev.label}
                          </Badge>
                        </div>
                        <p className="truncate text-xs font-medium text-foreground/80">{f.invoice}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setDismissed((prev) => new Set(prev).add(f.id))}
                        className="rounded p-1 text-muted-foreground hover:bg-background hover:text-foreground"
                        aria-label="Descartar"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <p className="text-sm leading-snug text-foreground/90">{f.detail}</p>
                    <div className="mt-2 rounded-md bg-background/70 p-2 text-xs">
                      <span className="font-semibold text-primary">Sugerencia: </span>
                      {f.suggestion}
                    </div>
                    <div className="mt-2 flex justify-end">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs"
                        onClick={() => handleGoTo(f.fileId)}
                      >
                        Ir a la factura
                        <ChevronRight className="ml-0.5 h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="mt-4 border-t pt-3 text-[11px] text-muted-foreground">
            Revisa las facturas de tu sesión actual. Descarta los hallazgos que ya hayas validado.
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default IssuedAIValidator;
